import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import * as React from 'react';
import { Col, Row, Tab, Tabs } from 'react-bootstrap';
import { AbstractControl, FieldConfig, FormGenerator, GroupProps, ValidatorFn, Validators } from 'react-reactive-form';
import { Redirect, RouteComponentProps } from 'react-router';
import { setSelectedMeasurementPointListByID } from '../../actions/measurementPointListActions';
import {
    clearHistoricalResultID,
    initSelectedResult,
    markGatePicked,
    submitMeasurementPointResult,
    updateMprAnswers
} from '../../actions/measurementPointResultsActions';
import { setSelectedWorkOrderID, toggleConfirmSelectJobModal } from '../../actions/workOrderActions';
import {
    IWorkOrder,
    IinstallBase,
    IinstallBasePopulated,
    Ijob,
    ImeasurementPoint,
    ImeasurementPointAnswer,
    ImeasurementPointList,
    ImeasurementPointListTab,
    ImeasurementPointResult,
    ImeasurementPointResultWithType,
    IopenRepairConflict,
    Iproduct,
    IproductInfo,
    Iuser
} from '../../models';
import {
    jobStatusEnum,
    jobTypesIdEnum,
    measurementPointAnswerTypes,
    measurementPointResultStatusTypesEnum,
    measurementPointTypesEnum
} from '../../models-enums';

import { TFunction } from 'i18next';
import moment from 'moment';
import { toastr } from 'react-redux-toastr';
import { getInventory, setSelectedProductByID } from '../../actions/manageInventoryActions';
import { getAssignedJobs } from '../../actions/manageJobActions';
import { constants } from '../../constants/constants';
import {
    findInUseGate,
    getSelectedOptionID,
    hideMeasurementPointControls,
    resolveTabGateVisibility
} from '../../actions/measurementPointInUseGateUtil';
import { translateMeasurementPointText, translateOptionLabels } from '../../actions/measurementPointTextUtil';
import { getAncestorIDs, translateResultToList } from '../../actions/measurementPointHistoryUtil';
import { resolveFormAnswers } from '../../actions/measurementPointSeedUtil';
import { FormUtil } from '../common/FormUtil';
import { FormUtilMeasurementPoints } from '../common/FormUtilMobile';
import { PartsControl } from '../parts/PartsControl';
import { PhotosControl } from '../photos/PhotosControl';
import { WorkOrderLayout } from '../workOrder/WorkOrderLayout';
import { getAllFormValues } from './MeasurementPointListFormUtils';
import { PointListButtonBarContainer } from './PointListButtonBarContainer';
import { resolveNextActiveTab } from './tabActivationUtil';

import { useSelector } from 'react-redux';
import { selectIsHistoricalMode, selectIsJobReadOnly } from '../../reducers/commonSelectors';
import { IinitialState } from '../../types/rootState';
import { BannerContainer } from '../common/BannerContainer';
import SAPDamageCodes from '../workOrder/SAP/SAPDamageCodes';
import { DeficiencyChecklist } from './DeficiencyChecklist';
import {
    DeficiencyGateOutcome,
    findComplianceValidationQuestion,
    findFailingMeasurementPoints,
    isComplianceQuestionGatedOff,
    outcomeAfterModalConfirm,
    readComplianceValidationAnswer,
    resolveDeficiencyGate
} from './deficiencyGate.helpers';
import { isDeficiencyChecklistJob } from './deficiencySuggestions.helpers';
import Measurements from './Mobile/Measurements';
import { TabType } from './Mobile/tabTypes';
import { getFormColumnWidths } from './formColumnWidthsUtil';
import { TabSelectorEntity } from './Mobile/TabSelector';
import { initialWorkOrder } from '../../reducers/initialState';
import MaintenanceMPControl from './MaintenanceMPControl';
import { MeasurementPointTabMenu } from './MeasurementPointTabMenu';
import { OpenRepairBlockInfo } from './OpenRepairBlockInfo';

const GroupHeader = ({ label, isMobile }: { label: string; isMobile: boolean }) => (
    <Col xs={12} className={isMobile ? '' : 'mp-row'}>
        <Row>
            <Col xs={12}>
                <h4 className={isMobile ? undefined : 'mp-header'}>{label}</h4>
            </Col>
        </Row>
    </Col>
);

interface GroupPropsEdited extends GroupProps {
    controls?: GroupProps;
}

interface Iprops extends RouteComponentProps<{ installID: string }> {
    loading: boolean;
    t: TFunction;
    productInfo: IproductInfo;
    submitMeasurementPointResult: typeof submitMeasurementPointResult;
    checkOpenRepairConflicts: (installBaseID: string) => Promise<IopenRepairConflict[]>;
    getUnseededMaintenanceTriggersForInstallBase: (installBaseID: string, listTabIDs: Set<string>) => number[];
    getInventory: typeof getInventory;
    getJobs: typeof getAssignedJobs;
    selectedInstall: IinstallBase;
    selectedJob: Ijob;
    measurementPointsByID: { [key: string]: ImeasurementPoint };
    selectedResult: ImeasurementPointResult;
    installBaseIDsNotTested: string[];
    allMeasurementPointLists: { [key: string]: ImeasurementPointList };
    clearHistoricalResultID: typeof clearHistoricalResultID;
    initSelectedResult: typeof initSelectedResult;
    historicalResultID: string;
    standardID: string;
    selectedProduct: Iproduct;
    setSelectedProductByID: typeof setSelectedProductByID;
    setSelectedMeasurementPointListByID: typeof setSelectedMeasurementPointListByID;
    isCommissioningTypeJob: boolean;
    isWorkOrderTypeJob: boolean;
    checklistMode: boolean;
    selectedWorkOrders: IWorkOrder[];
    selectedMeasurementPointList: ImeasurementPointList;
    toggleConfirmSelectJobModal: typeof toggleConfirmSelectJobModal;
    setSelectedWorkOrderID: typeof setSelectedWorkOrderID;
    historicalWorkOrderID: string;
    updateMprAnswers: typeof updateMprAnswers;
    markGatePicked: typeof markGatePicked;
    user: Iuser;
    installBasesNotTested: IinstallBasePopulated[];
    installBasesStatus: { [key: string]: number | undefined };
    clearSelectedHistoricalWorkOrderID: () => void;
    installBaseHistory: (IWorkOrder | ImeasurementPointResultWithType)[];
    allJobs: { [key: string]: Ijob };
    measurementPoinResultsIDs: string[];
    historicalMP: ImeasurementPointResult | IWorkOrder | undefined;
    isMobile: boolean;
    isTablet: boolean;
    decommissionAsset: { [key: string]: boolean };
    hasUnverifiedDeficiencies: boolean;
}

interface Istate {
    activeTabKey: TabType | number | string;
}

interface IFromState {
    forms: { [id: string]: AbstractControl };
    activeFormIndex?: number;
    longTextCheckboxes: { [index: string]: boolean };
}

const MeasurementPointListForm = (props: Iprops) => {
    // Note: This selector is no longer used since we now use install base latest result IDs directly
    // const installBaseMPResults = useSelector((state: any) =>
    //     selectMeasurementPointListResultsByInstallBaseID(state, props.selectedInstall.id)
    // );
    const measurementPointResultsByID = useSelector(
        (state: IinitialState) => state.measurementPointResults.measurementPointResultsByID
    );
    const installBasesByID = useSelector((state: IinitialState) => state.manageInventory.installBasesByID);

    // A past visit is on screen - see selectIsHistoricalMode, the one source of truth for it
    const isHistoricalMode = useSelector(selectIsHistoricalMode);
    const isJobReadOnly = useSelector(selectIsJobReadOnly);
    const isLocked = isHistoricalMode || isJobReadOnly;

    const getShouldHidePhotosTab = () => {
        return isLocked;
    };

    const getShouldHidePartsTab = (): boolean => {
        return (
            props.selectedJob.jobTypeID === jobTypesIdEnum.inspection ||
            props.selectedJob.jobTypeID === jobTypesIdEnum.audit ||
            props.selectedJob.jobTypeID === jobTypesIdEnum.commissioning ||
            props.selectedJob.jobTypeID === jobTypesIdEnum.agsRebalancing ||
            props.selectedJob.jobTypeID === jobTypesIdEnum.verification
        );
    };

    const [forms, setForms] = React.useState<IFromState>({
        forms: {},
        longTextCheckboxes: {}
    });
    const initialActiveTabKey = !getShouldHidePhotosTab()
        ? TabType.Photos
        : !getShouldHidePartsTab()
        ? TabType.Parts
        : TabType.Default;

    // Restore active tab from sessionStorage to persist across orientation changes
    // SSR safety: only access sessionStorage in browser environment
    const savedActiveTab = typeof window !== 'undefined' ? sessionStorage.getItem('beacon_active_tab') : null;
    // Convert numeric strings back to numbers to match TabType.Default = 0
    const restoredTab = savedActiveTab
        ? !isNaN(Number(savedActiveTab))
            ? Number(savedActiveTab)
            : savedActiveTab
        : initialActiveTabKey;

    const [state, setState] = React.useState<Istate>({
        activeTabKey: restoredTab
    });

    // Live In Use gate selections (gate MP ID -> selected option ID); drives hiding the tab's other controls
    const [gateSelections, setGateSelections] = React.useState<{ [gateID: string]: string | null }>({});

    /*
     * MMG-4405. When on, tabs hidden by a Not In Use gate come back and every In Use dropdown is
     * shown again so the engineer can change one. Per visit only - deliberately not persisted to
     * sessionStorage the way activeTabKey is, so each asset starts tidy.
     */
    const [showAllTabs, setShowAllTabs] = React.useState<boolean>(false);

    const filterMeasurementPoints = (tabID: string | number) => {
        return filter(
            props.measurementPointsByID,
            (measurementPoint: ImeasurementPoint) =>
                measurementPoint.measurementPointTabID === tabID &&
                !measurementPoint.isDeleted &&
                measurementPoint.type in measurementPointTypesEnum
        );
    };
    const filterTabs = (tabs: ImeasurementPointListTab[]) => {
        return filter(tabs, tab => tab.isDeleted !== true);
    };

    const filteredTabs: ImeasurementPointListTab[] = React.useMemo(
        () => filterTabs(props.selectedMeasurementPointList.measurementPointTabs),
        [props.selectedMeasurementPointList.measurementPointTabs]
    );

    const isObjectAMeasurementPointResult = (object: any): object is ImeasurementPointResultWithType => {
        return 'jobID' in object;
    };

    const { isWorkOrderTypeJob, selectedWorkOrders, setSelectedWorkOrderID: setSelectedWorkOrderIDProp } = props;

    const initializeWorkOrder = React.useCallback(
        (index = 0) => {
            if (isWorkOrderTypeJob && selectedWorkOrders.length) {
                const workOrder = selectedWorkOrders[index];
                if (workOrder) {
                    setSelectedWorkOrderIDProp(workOrder.id);
                }
            }
        },
        [isWorkOrderTypeJob, selectedWorkOrders, setSelectedWorkOrderIDProp]
    );

    const buildMeasurementPointAnswers = (formValues: {
        [key: string]: any;
    }): {
        answers: ImeasurementPointAnswer[];
        resultStatus: number;
        validationError: string;
    } => {
        let answers: ImeasurementPointAnswer[] = [];
        let resultStatus = measurementPointResultStatusTypesEnum.resultStatusPass;
        let validationError = '';
        forEach(formValues, (value: any, key: string) => {
            const measurementPoint = props.measurementPointsByID[key];

            // Process all measurement point types except for group
            if (
                (value !== undefined &&
                    measurementPoint &&
                    measurementPoint.type !== measurementPointTypesEnum.group) ||
                (value !== undefined && measurementPoint && measurementPoint.type >= measurementPointTypesEnum.date)
            ) {
                // check for moment and convert it to a string
                if (moment.isMoment(value)) {
                    value = value.toISOString();
                }
                const answerType = measurementPointAnswerTypes[measurementPoint.type];
                const rawValue = value !== null && typeof value === 'object' ? value.value : value;
                let answer: ImeasurementPointAnswer = {
                    measurementPointID: '',
                    showInReport: measurementPoint.showInReport
                };
                // check to see if the next value is a note that belongs with this answer
                // if the value is undefined, or it can not find a note it will also be undefined
                // TODO throw validation error if there is a note without a answer to go along with it
                if (formValues[`${key}_note`] !== undefined) {
                    answer = {
                        measurementPointID: key,
                        [String(answerType)]: rawValue,
                        notes: formValues[`${key}_note`],
                        showInReport: measurementPoint.showInReport
                    };
                } else {
                    answer = {
                        measurementPointID: key,
                        [String(answerType)]: rawValue,
                        showInReport: measurementPoint.showInReport
                    };
                }
                if (measurementPoint.type === measurementPointTypesEnum.passFail && rawValue === 2) {
                    resultStatus = measurementPointResultStatusTypesEnum.resultStatusFail;
                    if (!answer.notes && measurementPoint.allowNotes) {
                        // validationError = 'Missing notes on a failed test.'; // decided not to require notes BMA-498
                    }
                }
                // verify that the select option belongs to the measurement point
                if (measurementPoint.type === measurementPointTypesEnum.select && rawValue) {
                    if (
                        !measurementPoint.selectOptions ||
                        (measurementPoint.selectOptions &&
                            measurementPoint.selectOptions.find(item => item.id === rawValue) === undefined)
                    ) {
                        console.error(
                            '[buildMeasurementPointAnswers]: selection option value incorrect',
                            measurementPoint,
                            rawValue
                        );
                        validationError = 'Selection option value is incorrect.';
                    }
                }
                // For Long Text and Standard Text, we need to save the actual answer in the textValue field, which is the 2nd item in measurementPointAnswerTypes enum
                if (measurementPoint.type === measurementPointTypesEnum.longText && rawValue) {
                    let showInReport = measurementPoint.showInReport;
                    if (Object.keys(forms.longTextCheckboxes).includes(key)) {
                        showInReport = forms.longTextCheckboxes[key] ?? true;
                    }

                    answer = {
                        measurementPointID: key,
                        [String(measurementPointAnswerTypes[2])]: rawValue,
                        showInReport
                    };
                }
                answers = [...answers, answer];
            }
        });
        // if this is a maintenance or repair job, then set the resultStatus accordingly
        if (
            props.selectedJob.jobTypeID === jobTypesIdEnum.maintenance ||
            props.selectedJob.jobTypeID === jobTypesIdEnum.servicePlan
        ) {
            resultStatus = measurementPointResultStatusTypesEnum.resultStatusMaintain;
        }
        if (props.selectedJob.jobTypeID === jobTypesIdEnum.commissioning) {
            resultStatus = measurementPointResultStatusTypesEnum.resultStatusCommissioned;
        }
        if (props.selectedJob.jobTypeID === jobTypesIdEnum.repair) {
            resultStatus = measurementPointResultStatusTypesEnum.resultStatusRepaired;
        }

        return { answers, resultStatus, validationError };
    };

    const updateMPRAnswers = (key: string, value: string | null) => {
        if (!value) {
            return;
        }

        const { formValues } = getAllFormValues(forms.forms, false, (activeTabKey: any) => {
            setState(prev => ({ ...prev, activeTabKey }));
        });
        const { answers } = buildMeasurementPointAnswers({ ...formValues, [key]: value });
        props.updateMprAnswers(answers, seedKey);
    };

    /*
     * Push every current form value into redux before anything rebuilds the controls. updateMPRAnswers
     * skips cleared values, so without this a field the engineer cleared comes back on the rebuild.
     * shouldValidate is false: neither a gate selection nor a menu toggle may mark the forms submitted.
     */
    const syncFormValuesToRedux = (overrides: { [key: string]: any } = {}) => {
        const { formValues } = getAllFormValues(forms.forms, false);
        const { answers } = buildMeasurementPointAnswers({ ...formValues, ...overrides });
        props.updateMprAnswers(answers, seedKey);
    };

    /*
     * MMG-4405. Toggling rebuilds every tab's control config and reseeds each field from redux, so the
     * current values must be flushed first or a just-cleared field silently repopulates.
     */
    const handleToggleShowAllTabs = () => {
        syncFormValuesToRedux();
        setShowAllTabs(prev => !prev);
    };

    /*
     * subscribe to value changes for the form on this specific tab
     */
    const subscribeToValueChanges = (frms: any, id: string | number) => {
        const filteredMeasurementPoints = filterMeasurementPoints(id);
        const mpsWithoutGroups = filteredMeasurementPoints.filter(
            mp => mp.type !== measurementPointTypesEnum.group && mp.type !== measurementPointTypesEnum.maintenance
        );

        if (mpsWithoutGroups.length === 0) {
            return;
        }

        const inUseGate = findInUseGate(mpsWithoutGroups);

        forEach(mpsWithoutGroups, point => {
            const formControl = frms.forms[id]?.get(point.id);

            if (!formControl) {
                console.error('[subscribeToValueChanges]: missing form control for this measurement point', point, id);
                return;
            }

            formControl.valueChanges.subscribe((value: string | null) => {
                updateMPRAnswers(point.id, value);
            });

            if (inUseGate && point.id === inUseGate.id) {
                formControl.valueChanges.subscribe((value: any) => {
                    // Sync every current value to redux first (updateMPRAnswers skips cleared values) so the
                    // gate-driven control rebuild restores each field accurately, then re-evaluate visibility
                    syncFormValuesToRedux({ [point.id]: value });
                    props.markGatePicked(inUseGate.id);
                    const optionID = getSelectedOptionID(value);
                    setGateSelections(prev =>
                        prev[inUseGate.id] === optionID ? prev : { ...prev, [inUseGate.id]: optionID }
                    );
                });
            }

            if (point.allowNotes) {
                const key = `${point.id}_note`;
                frms.forms[id].get(key).valueChanges.subscribe((value: string | null) => {
                    updateMPRAnswers(key, value);
                });
            }
        });
    };

    /*
     * checkIfWorkOrdersValid
     * check to see if we have work orders
     * each work order must have closing notes
     * submit each WO form in order to trigger showing validtion errors
     */
    const checkIfWorkOrdersValid = () => {
        if (props.isWorkOrderTypeJob === false || props.selectedWorkOrders.length === 0) {
            return true;
        }
        let isValid = true;
        if (!isValid) {
            document.dispatchEvent(new CustomEvent('submitWorkOrderForms'));
        }
        return isValid;
    };

    /*
     * find the index of the current device.  then try to find the next device that does not have any results
     * also make sure it has a measurement point list
     */
    const getNextDeviceID = () => {
        const { installID } = props.match.params;
        const {
            // isCommissioningTypeJob,
            installBaseIDsNotTested
            // installBasesStatus
        } = props;
        // const filteredInstallBasesStatus = pick(installBasesStatus, installBaseIDsNotTested);
        const currentIndex = installBaseIDsNotTested.indexOf(installID);
        let nextIndex = 0;

        if (currentIndex !== -1) {
            nextIndex = currentIndex + 1;
        }

        // if the next index explodes the array then set the index to 0;
        if (nextIndex >= installBaseIDsNotTested.length) {
            nextIndex = 0;
        }
        const nextDeviceID = installBaseIDsNotTested[nextIndex];

        if (nextDeviceID === installID) {
            return '';
        }

        return nextDeviceID || '';
    };

    const getShouldHideSAPDamageCodeTab = () => {
        // First check if it's a warranty BM job type
        if (props.selectedJob.jobTypeID !== jobTypesIdEnum.warrantyBM) {
            return true; // Hide if not warranty BM
        }

        // Filter work orders to only those tied to the selected install base
        const installBaseWorkOrders = props.selectedWorkOrders.filter(
            wo => wo.installBaseID === props.selectedInstall.id
        );

        // Extract SAPBusinessIndicator values
        const sapBusinessIndicators = installBaseWorkOrders
            .map(wo => wo.sapBusinessIndicator)
            .filter(value => value !== undefined && value !== null);

        const distinctIndicators = Array.from(new Set(sapBusinessIndicators));

        // If the distinct list has only 1 entry and it's 'EC', hide the tab
        if (distinctIndicators.length === 1 && distinctIndicators[0] === 'EC') {
            return true; // Hide tab
        }

        return false; // Show tab
    };

    const getWorkOrderTitle = (workOrder: IWorkOrder | undefined, t: TFunction): string => {
        if (!workOrder) {
            return '';
        }
        const checklist = workOrder.preventativeMaintenanceChecklist;
        const pmpWorkOrderTitle = checklist ? checklist.name : `${t('manageWorkOrder:maintenanceTabTitle')}`;
        const repairWorkOrderTitle = `${t('manageWorkOrder:repairTabTitle')}${
            workOrder.number ? `: ${workOrder.number}` : ''
        }`;
        const warrantyWorkOrderTitle = `${t('manageInventory:warranty')}: ${workOrder.number}`;
        const servicePlanWorkOrderTitle = `${t('manageWorkOrder:servicePlanTabTitle')} ${
            workOrder.number ? `: ${workOrder.number}` : ''
        }`;
        const commissioningWorkOrderTitle = `${t('manageWorkOrder:commissioningTabTitle')}${
            workOrder.number ? `: ${workOrder.number}` : ''
        }`;
        const verificationWorkOrderTitle = `${t('manageWorkOrder:verificationTabTitle')}${
            workOrder.number ? `: ${workOrder.number}` : ''
        }`;
        switch (props.selectedJob.jobTypeID) {
            case jobTypesIdEnum.maintenance:
                return pmpWorkOrderTitle;
            case jobTypesIdEnum.warrantyBM:
                return warrantyWorkOrderTitle;
            case jobTypesIdEnum.repair:
                return repairWorkOrderTitle;
            case jobTypesIdEnum.servicePlan:
                return servicePlanWorkOrderTitle;
            case jobTypesIdEnum.commissioning:
                return commissioningWorkOrderTitle;
            case jobTypesIdEnum.verification:
                return verificationWorkOrderTitle;
            default:
                return repairWorkOrderTitle;
        }
    };

    const isUserCopyingHistory = (): boolean => {
        if (props.historicalMP !== undefined && props.historicalMP.id !== '') {
            return true;
        }

        return false;
    };

    // This is a callback function for when a user clicks "Hide in Report" checkbox on a Long Text MP
    const showInReportHandler = (id: string) => {
        if (id !== '') {
            return (event: React.ChangeEvent<HTMLInputElement>) => {
                if (forms.longTextCheckboxes[id] === undefined) {
                    const mp = props.measurementPointsByID[id];
                    const show = mp?.showInReport ?? true;
                    const newCheckbox = {
                        [id]: !show
                    };

                    setForms({ ...forms, longTextCheckboxes: { ...forms.longTextCheckboxes, ...newCheckbox } });
                } else {
                    const newCheckbox = {
                        [id]: !forms.longTextCheckboxes[id]
                    };

                    setForms({ ...forms, longTextCheckboxes: { ...forms.longTextCheckboxes, ...newCheckbox } });
                }

                const { formValues } = getAllFormValues(forms.forms, false, (activeTabKey: any) => {
                    setState(prev => ({ ...prev, activeTabKey }));
                });
                const { answers } = buildMeasurementPointAnswers({ ...formValues });
                // Find the index of the answer for this long text MP
                const index = answers.findIndex((answer: any) => answer.measurementPointID === id);
                const currentAnswer = answers[index];
                if (index !== -1 && currentAnswer) {
                    currentAnswer.showInReport = !currentAnswer.showInReport;
                    props.updateMprAnswers(answers, seedKey);
                }
            };
        }
    };

    // Function to get the last reading value for a specific measurement point using install base's latest result ID
    const getLastReadingValue = (measurementPointID: string): { value: any; resultId: string } | null => {
        try {
            const currentInstallBase = installBasesByID[props.selectedInstall.id];
            if (!currentInstallBase) {
                return null;
            }

            // Get the appropriate latest result ID field based on the current job type
            let latestResultIDField: keyof IinstallBase;
            switch (props.selectedJob.jobTypeID) {
                case jobTypesIdEnum.audit:
                    latestResultIDField = 'latestAuditMeasurementPointListResultID';
                    break;
                case jobTypesIdEnum.agsRebalancing:
                    latestResultIDField = 'latestAGSMeasurementPointListResultID';
                    break;
                case jobTypesIdEnum.verification:
                    latestResultIDField = 'latestVerificationMeasurementPointListResultID';
                    break;
                case jobTypesIdEnum.commissioning:
                    latestResultIDField = 'latestCommissioningMeasurementPointListResultID';
                    break;
                case jobTypesIdEnum.maintenance:
                    latestResultIDField = 'latestMaintenanceMeasurementPointListResultID';
                    break;
                case jobTypesIdEnum.repair:
                    latestResultIDField = 'latestRepairMeasurementPointListResultID';
                    break;
                case jobTypesIdEnum.servicePlan:
                    latestResultIDField = 'latestServicePlanMeasurementPointListResultID';
                    break;
                case jobTypesIdEnum.warrantyBM:
                    latestResultIDField = 'latestWarrantyMeasurementPointListResultID';
                    break;
                case jobTypesIdEnum.inspection:
                default:
                    latestResultIDField = 'latestMeasurementPointListResultID';
                    break;
            }

            const latestResultID = currentInstallBase[latestResultIDField] as string;

            if (!latestResultID) {
                return null;
            }

            // Get the latest result from the Redux store
            const latestResult = measurementPointResultsByID[latestResultID];
            if (!latestResult) {
                return null;
            }
            if (!latestResult.measurementPointAnswers) {
                return null;
            }

            const acceptablePointIDs = [
                measurementPointID,
                ...getAncestorIDs(measurementPointID, props.measurementPointsByID)
            ];
            const foundAnswer = latestResult.measurementPointAnswers.find(
                (answer: ImeasurementPointAnswer) =>
                    answer &&
                    acceptablePointIDs.includes(answer.measurementPointID) &&
                    ((answer.numericValue !== null && answer.numericValue !== undefined) ||
                        (answer.textValue !== null && answer.textValue !== undefined && answer.textValue !== '') ||
                        (answer.dateValue !== null && answer.dateValue !== undefined))
            );

            if (foundAnswer) {
                // Return the appropriate value based on what's available
                if (foundAnswer.numericValue !== null && foundAnswer.numericValue !== undefined) {
                    return {
                        value: foundAnswer.numericValue,
                        resultId: latestResult.id
                    };
                } else if (
                    foundAnswer.textValue !== null &&
                    foundAnswer.textValue !== undefined &&
                    foundAnswer.textValue !== ''
                ) {
                    return {
                        value: foundAnswer.textValue,
                        resultId: latestResult.id
                    };
                } else if (foundAnswer.dateValue !== null && foundAnswer.dateValue !== undefined) {
                    return {
                        value: foundAnswer.dateValue,
                        resultId: latestResult.id
                    };
                }
            }

            return null;
        } catch (error) {
            return null;
        }
    };

    const installBaseHistoryKey = (props.installBaseHistory ?? []).map(item => item.id).join(',');

    const { formControls, gateHiddenTabIDs, gateHideableTabIDs, gateNotInUseTabIDs, seedKey } = React.useMemo<{
        formControls: { [key: string]: FieldConfig };
        gateHiddenTabIDs: ReadonlySet<string | number>;
        gateHideableTabIDs: ReadonlySet<string | number>;
        gateNotInUseTabIDs: ReadonlySet<string | number>;
        seedKey: string | undefined;
    }>(() => {
        const { isMobile } = props;
        const selectedResult =
            translateResultToList(props.selectedResult, props.selectedMeasurementPointList, props.measurementPointsByID) ||
            props.selectedResult;
        const { measurementPointAnswers } = selectedResult;
        let defaultControls = {};
        /*
         * Two different sets, and they are not interchangeable (MMG-4405):
         *   - hiddenTabIDs is VIEW state - which tabs the bar leaves out. It folds in showAllTabs and
         *     isHistoricalMode, so it changes when a menu is toggled.
         *   - notInUseTabIDs is GATE TRUTH - which tabs the engineer declared Not In Use, regardless
         *     of what is on screen. Anything deciding what the engineer may DO reads this one.
         */
        const hiddenTabIDs = new Set<string | number>();
        const hideableTabIDs = new Set<string | number>();
        const notInUseTabIDs = new Set<string | number>();

        let newControl: Record<string, any> = {};
        /*
         * Every historical view is read only, a work order row included: what is on screen was
         * recorded on a closed job, so answering it here would write this job's result from
         * another visit's form (MMG-4345).
         */
        const disabled = isLocked;
        const history = props.installBaseHistory;
        let lastMP: IWorkOrder | ImeasurementPointResultWithType | undefined;
        let historicDataToUse: any = undefined;

        // If the user selected a historical item, and clicked Copy History, this is how we load up those answers
        if (isObjectAMeasurementPointResult(props.historicalMP)) {
            historicDataToUse = { ...props.historicalMP, measurementPointListType: 0 };
        }

        lastMP = undefined;
        if (historicDataToUse !== undefined && historicDataToUse.id !== '') {
            lastMP = historicDataToUse;
        } else if (history !== undefined && history.length > 0) {
            for (const value of history) {
                // Check if the value is an IWorkOrder or ImeasurementPointResultWithType
                if (isObjectAMeasurementPointResult(value)) {
                    const tempMPR = value as ImeasurementPointResultWithType;
                    // Make sure the item in History, is of the same job type we are currently on
                    if (tempMPR.jobTypeID !== undefined) {
                        // This can happen if you save and log the same MP multiple times without closing the job
                        if (tempMPR.jobTypeID === '00000000-0000-0000-0000-000000000000') {
                            // To be safe, let's see if we can find the job in state and get it's real jobTypeID
                            const tempJob = props.allJobs[tempMPR.jobID];
                            if (tempJob !== undefined) {
                                // If job was found, see if jobType is the same
                                if (props.selectedJob.jobTypeID === tempJob.jobTypeID) {
                                    lastMP = value as ImeasurementPointResultWithType;
                                    break;
                                }
                            }
                        } else {
                            // If the item in history does have a jobTypeID set, check if it's the same job type
                            if (props.selectedJob.jobTypeID === tempMPR.jobTypeID) {
                                lastMP = value as ImeasurementPointResultWithType;
                                break;
                            }
                        }
                    }
                } else {
                    // this is an IWorkOrder, we don't care about you, only inspection jobs
                    lastMP = undefined;
                }
            }
        }

        if (lastMP !== undefined && isObjectAMeasurementPointResult(lastMP)) {
            lastMP = translateResultToList(lastMP, props.selectedMeasurementPointList, props.measurementPointsByID);
        }
        const isFirstVisit = props.selectedResult.temporary === true && lastMP === undefined;

        // A draft is filled from the last visit once; every rebuild after that reads the engineer's answers
        const { answers: cleanedAnswers, seedKey: resolvedSeedKey } = resolveFormAnswers({
            temporary: props.selectedResult.temporary,
            answersSeededFrom: props.selectedResult.answersSeededFrom,
            currentAnswers: measurementPointAnswers,
            lastResult: lastMP !== undefined && isObjectAMeasurementPointResult(lastMP) ? lastMP : undefined,
            measurementPointsByID: props.measurementPointsByID,
            isCopyingHistory: isUserCopyingHistory()
        });
        forEach(filteredTabs, (tab: ImeasurementPointListTab) => {
            const filteredMeasurementPoints = filterMeasurementPoints(tab.id);
            let tabControls = {};

            /*
             * In Use gate: while the dropdown is not set to In Use, every other control on the tab is
             * hidden (MMG-4268); once it has been answered, the dropdown itself is hidden too (MMG-4405).
             * Visibility only - values and saved answers are untouched. Both decisions and the value the
             * dropdown displays come from one resolver so they cannot be fed different option IDs; the
             * distinction between them is documented on resolveTabGateVisibility.
             */
            const {
                gate: inUseGate,
                selectedOptionID: gateSelectedOptionID,
                hideNonGateControls,
                hideGateControl,
                isTabHidden,
                isTabHideable,
                isGateNotInUse
            } = resolveTabGateVisibility(
                filteredMeasurementPoints,
                cleanedAnswers,
                gateSelections,
                showAllTabs,
                isHistoricalMode,
                isFirstVisit,
                props.selectedResult.pickedGateIDs ?? []
            );

            if (isTabHidden) {
                hiddenTabIDs.add(tab.id);
            }

            if (isTabHideable) {
                hideableTabIDs.add(tab.id);
            }

            if (isGateNotInUse) {
                notInUseTabIDs.add(tab.id);
            }

            filteredMeasurementPoints.forEach((measurementPoint: ImeasurementPoint) => {
                // determin the default value.  Might be a value set by the MeasurementPoint or by the most recent result.
                const {
                    allowNotes,
                    guideText,
                    selectOptions,
                    selectDefaultOptionID,
                    id,
                    passFailDefault,
                    label,
                    isRequired,
                    numericMinValue,
                    numericMaxValue,
                    type,
                    defaultText
                } = measurementPoint;
                let defaultValue = null;
                let noteDefaultValue = null;
                let defaultShowInReport = true;
                const convertedOptions =
                    selectOptions && selectOptions.length
                        ? translateOptionLabels(FormUtil.convertToOptions(selectOptions), props.t)
                        : [];

                if (type === measurementPointTypesEnum.passFail && !!passFailDefault) {
                    defaultValue = passFailDefault;
                }

                if (type === measurementPointTypesEnum.select && !!selectDefaultOptionID && convertedOptions.length) {
                    defaultValue = convertedOptions.find(option => option.value === selectDefaultOptionID);
                }

                const foundAnswer = cleanedAnswers.find(answer => answer.measurementPointID === id);

                if (foundAnswer !== undefined) {
                    if (type === measurementPointTypesEnum.numeric) {
                        defaultValue = foundAnswer?.numericValue;
                    }
                    if (type === measurementPointTypesEnum.passFail) {
                        defaultValue = foundAnswer?.pass;
                    }
                    if (type === measurementPointTypesEnum.text) {
                        defaultValue = foundAnswer?.textValue;
                    }
                    if (type === measurementPointTypesEnum.date) {
                        defaultValue = foundAnswer?.dateValue;
                    }
                    if (type === measurementPointTypesEnum.select) {
                        defaultValue = convertedOptions.find(
                            option => option.value === foundAnswer?.measurementPointSelectOptionID
                        );
                    }
                    if (type === measurementPointTypesEnum.longText) {
                        defaultValue = foundAnswer?.textValue;

                        if (Object.keys(forms.longTextCheckboxes).includes(foundAnswer.measurementPointID)) {
                            defaultShowInReport = forms.longTextCheckboxes[foundAnswer.measurementPointID] ?? true;
                        }
                    }

                    if (measurementPoint.allowNotes !== undefined) {
                        noteDefaultValue = foundAnswer?.notes;
                    }
                } else {
                    // For Long Text, if no answer exists, use the default text
                    if (type === measurementPointTypesEnum.longText) {
                        defaultValue = defaultText;
                        defaultShowInReport = true; // always default to showing in the report if no answer exists
                    }
                }

                /*
                 * The gate keeps the option that is driving hideNonGateControls. Changing the gate rebuilds
                 * every control on the tab, and cleanedAnswers can hold the previous inspection's remembered
                 * answer, which would otherwise snap the gate back to that value on the rebuild.
                 */
                if (inUseGate && id === inUseGate.id && type === measurementPointTypesEnum.select) {
                    defaultValue = convertedOptions.find(option => option.value === gateSelectedOptionID) ?? null;
                }

                const columnWidths = getFormColumnWidths(
                    props.isMobile,
                    allowNotes,
                    guideText,
                    measurementPoint.type
                );

                // Get last reading value for showLastValue feature (for numeric, text, and date measurement points)
                let lastReadingData = null;
                if (
                    measurementPoint &&
                    measurementPoint.showLastValue &&
                    (measurementPoint.type === measurementPointTypesEnum.numeric ||
                        measurementPoint.type === measurementPointTypesEnum.text ||
                        measurementPoint.type === measurementPointTypesEnum.date) &&
                    id
                ) {
                    lastReadingData = getLastReadingValue(id);
                }

                // build the Header or an actual form control
                if (measurementPoint.type === measurementPointTypesEnum.group) {
                    newControl = {
                        [`$field_${id}`]: {
                            isStatic: false, // ensures a key is added
                            render: () => <GroupHeader label={label} key={id} isMobile={isMobile} />
                        } as GroupPropsEdited
                    };
                } else if (measurementPoint.type === measurementPointTypesEnum.maintenance) {
                    newControl = {
                        [`$field_${id}`]: {
                            isStatic: false,
                            render: () => (
                                <MaintenanceMPControl
                                    key={id}
                                    mp={measurementPoint}
                                    installBaseID={props.selectedInstall.id}
                                    disabled={disabled}
                                />
                            )
                        } as GroupPropsEdited
                    };
                } else {
                    let validators: ValidatorFn[] = [];
                    if (measurementPoint.type === measurementPointTypesEnum.date) {
                        validators = [FormUtil.validators.isValidMoment];
                    }
                    if (isRequired) {
                        validators = [...validators, FormUtil.validators.requiredWithTrim];
                    }
                    if (measurementPoint.type === measurementPointTypesEnum.numeric) {
                        if (numericMaxValue) {
                            validators = [...validators, Validators.max(numericMaxValue)];
                        }
                        if (numericMinValue) {
                            validators = [...validators, Validators.min(numericMinValue)];
                        }
                    }

                    if (
                        measurementPoint.type === measurementPointTypesEnum.summaryPage ||
                        measurementPoint.type === measurementPointTypesEnum.longText
                    ) {
                        validators = [...validators, FormUtil.validators.maxLength(1000)];
                    }

                    newControl = {
                        [id]: {
                            render: FormUtilMeasurementPoints[
                                measurementPointTypesEnum[
                                    measurementPoint.type
                                ] as keyof typeof FormUtilMeasurementPoints
                            ](isMobile),
                            meta: {
                                charLimit: 10000,
                                colWidth: 6,
                                columnWidths,
                                name: label,
                                label: translateMeasurementPointText(label, props.t),
                                required: isRequired || measurementPoint.type === measurementPointTypesEnum.passFail,
                                options: convertedOptions,
                                id,
                                passFailDefault,
                                guideText: measurementPoint.guideText,
                                helpText: measurementPoint.helpText,
                                isClearable: true,
                                defaultText: measurementPoint.defaultText,
                                showNormalToolbar: false,
                                initialContent: defaultValue,
                                showInReport: defaultShowInReport, // this is for MP answer, not the MP itself
                                showInReportHandler, // only used for long text
                                showLastValue: measurementPoint.showLastValue,
                                lastReadingData,
                                t: props.t
                            },
                            options: {
                                validators
                            },
                            formState: { value: defaultValue, disabled }
                        } as GroupPropsEdited
                    };

                    if (measurementPoint.allowNotes) {
                        newControl = {
                            ...newControl,
                            [id]: {
                                ...newControl[id],
                                meta: {
                                    ...newControl[id].meta,
                                    colWidth: 10
                                }
                            }
                        };

                        newControl = {
                            ...newControl,
                            [`${id}_note`]: {
                                render: FormUtilMeasurementPoints.NewMeasurementPointNote,
                                meta: {
                                    name: `${id}_note`,
                                    colWidth: 2,
                                    label: `${label} note`,
                                    t: props.t,
                                    isHistoricalNote: isLocked,
                                    measurementPointID: id,
                                    installBaseID: props.selectedInstall.id
                                },
                                formState: {
                                    value: noteDefaultValue,
                                    disabled
                                } // WILL NOT WORK WITHOUT DISABLED
                            }
                        };
                    }
                }

                if (inUseGate && measurementPoint.id === inUseGate.id) {
                    if (hideGateControl) {
                        newControl = hideMeasurementPointControls(newControl);
                    }
                } else if (hideNonGateControls && inUseGate) {
                    newControl = hideMeasurementPointControls(newControl);
                }

                tabControls = { ...tabControls, ...newControl } as {
                    [key: string]: GroupPropsEdited;
                };
            });
            const tabConfig = { controls: tabControls } as FieldConfig;
            defaultControls = { ...defaultControls, [tab.id]: tabConfig };
        });
        return {
            formControls: defaultControls,
            gateHiddenTabIDs: hiddenTabIDs,
            gateHideableTabIDs: hideableTabIDs,
            gateNotInUseTabIDs: notInUseTabIDs,
            seedKey: resolvedSeedKey
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        filteredTabs,
        props.selectedMeasurementPointList.id,
        props.selectedResult.id,
        props.selectedInstall.id,
        forms.longTextCheckboxes,
        gateSelections,
        showAllTabs,
        props.selectedResult.pickedGateIDs,
        // a same-type visit arriving after first render turns a first visit into a repeat one
        installBaseHistoryKey,
        props.historicalMP?.id,
        // opening or closing a historical row is what makes the controls read only, and a work
        // order row leaves selectedResult alone, so this cannot be left out (MMG-4345)
        isLocked
    ]);

    const additionalTabs: TabSelectorEntity[] = React.useMemo(() => {
        const tabs: TabSelectorEntity[] = [];
        props.selectedWorkOrders.forEach((selectedWorkOrder: IWorkOrder) => {
            const woTitle = getWorkOrderTitle(selectedWorkOrder, props.t);
            tabs.push({
                title: woTitle,
                id: selectedWorkOrder.id,
                isWorkOrder: true,
                isMeasurementPoint: false,
                index: -1
            });
        });

        filteredTabs.forEach((measurementPointTab: ImeasurementPointListTab, index: number) => {
            tabs.push({
                title: measurementPointTab.name,
                id: measurementPointTab.id,
                isWorkOrder: false,
                isMeasurementPoint: true,
                index,
                isChecklistTab: measurementPointTab.isChecklistTab === true
            });
        });

        return tabs;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectedWorkOrders, filteredTabs]);

    /*
     * MMG-4405. additionalTabs stays the full list because it drives the rendered tab panels, and a
     * panel that stops rendering loses its answers - updateMprAnswers rebuilds the answer array from
     * mounted forms only. visibleTabs drives the tab bar alone. No showAllTabs check here: the
     * resolver already folded showAllTabs and isHistoricalMode into isTabHidden.
     */
    const visibleTabs: TabSelectorEntity[] = React.useMemo(
        () => additionalTabs.filter((tab: TabSelectorEntity) => !gateHiddenTabIDs.has(tab.id)),
        [additionalTabs, gateHiddenTabIDs]
    );

    // Kept while Show All Tabs is on, so it can always be switched off again
    const hasNotInUseTabs = gateHideableTabIDs.size > 0 || showAllTabs;

    /*
     * MMG-4335 pathways 1A / 1B / 2C. Every dialog resolves to onOk (ESC and Enter do too), so no
     * keypress can silently save; continuing is always an explicit click.
     */
    const runDeficiencyOutcome = (
        outcome: DeficiencyGateOutcome,
        failing: ImeasurementPoint[],
        finalize: (overrideOpenRepair: boolean, complianceConfirmed?: boolean) => void,
        questionTabName?: string
    ) => {
        if (outcome.kind === 'save') {
            finalize(false, outcome.compliance === 'pass');
            return;
        }

        if (outcome.kind === 'blocked') {
            toastr.warning(
                props.t('toastMessage:warning'),
                questionTabName
                    ? props.t('measurementPoints:complianceAnswerRequiredTab', { tabName: questionTabName })
                    : props.t('measurementPoints:complianceAnswerRequired'),
                constants.toastrWarning
            );
            return;
        }

        const proceed = () => {
            const next = outcomeAfterModalConfirm(outcome.modal);
            if (next.kind === 'save') {
                finalize(false, next.compliance === 'pass');
            }
        };

        if (outcome.modal === 'recordedFailures') {
            // acknowledge-only: the engineer must go back and resolve or re-answer
            toastr.confirm(
                props.t('measurementPoints:recordedFailuresBody', {
                    measurementPoints: failing.map(point => point.label).join(', ')
                }),
                { disableCancel: true, okText: props.t('common:ok'), onOk: () => null }
            );
            return;
        }

        const bodyKey =
            outcome.modal === 'unaddressedDeficiencies'
                ? 'measurementPoints:unaddressedDeficienciesBody'
                : 'measurementPoints:noRecordedFailuresBody';

        toastr.confirm(props.t(bodyKey), {
            disableCancel: true,
            okText: props.t('common:no'),
            onOk: () => null,
            buttons: [{ text: props.t('common:yes'), handler: proceed }, { ok: true }]
        });
    };

    const handleSubmit = (
        e?: React.FormEvent<HTMLFormElement>,
        statusOverride?: measurementPointResultStatusTypesEnum
    ) => {
        if (e) {
            e.preventDefault();
        }

        let detailedErrorShown = false;
        const { formStatus, formValues } = getAllFormValues(
            forms.forms,
            true,
            (activeTabKey: any, lastErroredMeasurementPointLabel: string) => {
                detailedErrorShown = true;
                setState(prev => ({ ...prev, activeTabKey }));
                const tab: TabSelectorEntity | undefined = additionalTabs.find(
                    (candidateTab: TabSelectorEntity) => candidateTab.id === activeTabKey
                );

                // Ensure tab exists before trying to access its properties
                if (tab !== undefined) {
                    toastr.error(
                        `There is a mandatory field which was not filled in: Go to tab \`${tab.title}\` and see measruement point \`${lastErroredMeasurementPointLabel}\`.`,
                        '',
                        constants.toastrError
                    );
                    return;
                }
                // Handle the case when tab is undefined
                toastr.error('Unexpected error: The selected tab does not exist.', '', constants.toastrError);
            }
        );

        // if the form is invalid OR we are overiding the status which means we are marking this as Cannot Complete
        const shouldSkipValidation =
            statusOverride === measurementPointResultStatusTypesEnum.resultStatusCannotComplete;
        if ((checkIfWorkOrdersValid() === false || formStatus === 'INVALID') && shouldSkipValidation === false) {
            // the generic toast is redundant when a detailed mandatory-field toast already fired
            if (!detailedErrorShown) {
                toastr.error('Please check invalid inputs', '', constants.toastrError);
            }
            return;
        }
        const { answers, resultStatus, validationError } = buildMeasurementPointAnswers(formValues);
        if (validationError.length) {
            toastr.warning('Warning', validationError, constants.toastrError);
            return;
        }
        // Tabs on the list being saved. Guards below only act on MPs the tech can see (MMG-4430).
        const listTabIDs = new Set(
            (props.selectedMeasurementPointList.measurementPointTabs || []).map(tab => tab.id)
        );
        // Seeding guard: block Save & Log for an enabled, never-maintained trigger with no recorded
        // maintenance and no escape valve. Like mandatory-field validation, jump to the offending
        // trigger's tab and explain. Skipped on Cannot Complete.
        if (!shouldSkipValidation) {
            const unseededTypes = props.getUnseededMaintenanceTriggersForInstallBase(
                props.match.params.installID,
                listTabIDs
            );
            if (unseededTypes.length) {
                const unseededMPs = Object.values(props.measurementPointsByID).filter(
                    mp =>
                        mp.type === measurementPointTypesEnum.maintenance &&
                        mp.maintenanceType !== undefined &&
                        !mp.isDeleted &&
                        unseededTypes.includes(mp.maintenanceType) &&
                        listTabIDs.has(mp.measurementPointTabID)
                );
                /*
                 * MMG-4405. A trigger sitting on a tab whose In Use gate is answered Not In Use cannot
                 * be satisfied: MMG-4268 already hides the maintenance control there, and MMG-4405 now
                 * hides the tab as well, so the block is an unreachable wall rather than a real check.
                 * Follows the isComplianceQuestionGatedOff precedent above - a Not-In-Use asset counts
                 * as gated off - and reads gateNotInUseTabIDs, never gateHiddenTabIDs: whether Save &
                 * Log is allowed must not depend on whether Show All Tabs happens to be toggled on.
                 */
                const offendingMP = unseededMPs.find(mp => !gateNotInUseTabIDs.has(mp.measurementPointTabID));
                const everyTriggerGatedOff = unseededMPs.length > 0 && offendingMP === undefined;

                if (!everyTriggerGatedOff) {
                    const offendingTabID = offendingMP?.measurementPointTabID;
                    if (offendingTabID) {
                        setState(prev => ({ ...prev, activeTabKey: offendingTabID }));
                    }
                    const offendingTab = additionalTabs.find(tab => tab.id === offendingTabID);
                    const typeLabels = unseededTypes
                        .map(mt => constants.maintenanceTypeOptions.find(o => o.value === mt)?.label || String(mt))
                        .join(', ');
                    const location = offendingTab ? ` Go to tab "${offendingTab.title}".` : '';
                    toastr.error(
                        `Maintenance not recorded for ${typeLabels}.${location} Select Yes for Was ` +
                            `Maintenance Completed, enter a Date Last Maintained, check Unknown, or disable the trigger.`,
                        '',
                        constants.toastrError
                    );
                    return;
                }
            }
        }
        let status = statusOverride ? statusOverride : resultStatus;

        // 1. Look up redux, does the install base Id exist in the decommissionAsset object?
        const shouldDecommission = Object.keys(props.decommissionAsset).includes(props.match.params.installID);
        if (shouldDecommission) {
            // 2. If it does, check if it's true
            const value = props.decommissionAsset[props.match.params.installID];
            if (value) {
                // 3. If true, then override status to be status of Decommission check enum
                status = measurementPointResultStatusTypesEnum.resultStatusDecommissioned;
            }
        }

        const result = {
            ...props.selectedResult,
            installBaseID: props.match.params.installID,
            measurementPointListID: props.selectedMeasurementPointList.id,
            status
        };

        // Please make sure jobTypeID is set
        if (result.jobTypeID === undefined) {
            result.jobTypeID = props.selectedJob.jobTypeID;
        }

        const finalizeSubmit = (overrideOpenRepair: boolean, complianceConfirmed?: boolean) => {
            // TODO might need to block submitting this while the work orders are still being updated with closing notes
            props.submitMeasurementPointResult(
                result,
                answers,
                props.selectedWorkOrders,
                overrideOpenRepair,
                complianceConfirmed
            );
            const nextDeviceID = getNextDeviceID();

            setForms({
                forms: {},
                longTextCheckboxes: {}
            });
            // TODO: workaround; nextDeviceID sometimes returns current device ID
            if (nextDeviceID.length && nextDeviceID !== props.match.params.installID) {
                props.history.push(`/devices/${nextDeviceID}`);
                toastr.success('Success', 'Saved tests and loaded next device.', constants.toastrSuccess);
            } else {
                toastr.success('Success', 'All selected devices have been tested.', constants.toastrSuccess);
                props.history.push('/devices');
            }
        };

        /*
         * MMG-4335: repair-type jobs run the deficiency gate instead of the open-repair pre-check.
         * MMG-4460: Repair only. This has to move with the render above it - leave the gate wider
         * than the checklist and a Service Plan engineer is told to clear deficiencies they have no
         * screen to clear.
         */
        if (isDeficiencyChecklistJob(props.selectedJob.jobTypeID)) {
            const question = findComplianceValidationQuestion(props.measurementPointsByID, listTabIDs);

            // no configured question means no gate - most lists have none, and those saves must not break.
            // a "Not in Use" asset hides the question, so gating on it would strand the engineer.
            // decommissioning is exempt for the same reason Cannot Complete is: a retired asset has
            // nothing left to validate.
            const isDecommissioning = status === measurementPointResultStatusTypesEnum.resultStatusDecommissioned;
            const gatedOff =
                question !== undefined &&
                isComplianceQuestionGatedOff(question, props.measurementPointsByID, answers, gateSelections);

            if (!question || shouldSkipValidation || gatedOff || isDecommissioning) {
                finalizeSubmit(false);
                return;
            }

            const failing = findFailingMeasurementPoints(props.measurementPointsByID, answers);
            const outcome = resolveDeficiencyGate({
                answer: readComplianceValidationAnswer(question, answers),
                hasUnverifiedDeficiencies: props.hasUnverifiedDeficiencies,
                hasFailingMeasurementPoints: failing.length > 0
            });

            const questionTab = (props.selectedMeasurementPointList.measurementPointTabs || []).find(
                tab => tab.id === question.measurementPointTabID
            );
            runDeficiencyOutcome(outcome, failing, finalizeSubmit, questionTab?.name);
            return;
        }

        if (props.selectedJob.jobTypeID !== jobTypesIdEnum.inspection) {
            finalizeSubmit(false);
            return;
        }

        // warn before queueing; offline the API's 409 at sync time is what surfaces the block
        props.checkOpenRepairConflicts(props.match.params.installID).then(conflicts => {
            if (!conflicts.length) {
                finalizeSubmit(false);
                return;
            }

            const jobNumbers = conflicts.map(conflict => conflict.jobNumber).join(', ');
            toastr.confirm(props.t('measurementPoints:openRepairBlock', { jobNumber: jobNumbers }), {
                // ESC and Enter both resolve to onOk, so the override stays click-only
                disableCancel: true,
                component: OpenRepairBlockInfo,
                okText: props.t('common:ok'),
                onOk: () => null,
                buttons: [
                    {
                        text: props.t('measurementPoints:overrideOpenRepairBlock'),
                        handler: () => finalizeSubmit(true)
                    },
                    { ok: true }
                ]
            });
        });
    };

    /*
     * require notes, skip validation, set status to cannot complete
     */
    const handleCannotComplete = (e?: any) => {
        if (!props.selectedResult.notes.length) {
            toastr.warning(
                'Warning',
                'Please enter why you cannot complete the test in the comments to the left.',
                constants.toastrWarning
            );
            return;
        }

        handleSubmit(e, measurementPointResultStatusTypesEnum.resultStatusCannotComplete);
    };

    const handleSelect = (activeTabKey: any | TabType | number | string) => {
        // SSR safety: only access sessionStorage in browser environment
        if (typeof window !== 'undefined') {
            sessionStorage.setItem('beacon_active_tab', String(activeTabKey));
        }
        setState(prev => ({ ...prev, activeTabKey }));
        initializeWorkOrder(activeTabKey);
    };


    React.useEffect(() => {
        if (props.selectedInstall.isDeleted) {
            toastr.warning(
                props.t('toastMessage:warning'),
                props.t('toastMessage:assetDeleted'),
                constants.toastrWarning
            );
        }
        if (!props.selectedInstall.id.length) {
            return;
        }

        // verify that we are editing a device that belongs to this facility
        if (props.selectedInstall.facilityID !== props.selectedJob.facilityID) {
            props.history.push('/jobs');
        }

        // PointListButtonBar needs the active work order to be set
        initializeWorkOrder();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { initSelectedResult: initSelectedResultProp, selectedInstall, selectedJob, history } = props;

    //  Handle install/job changes (initialization)
    React.useEffect(() => {
        // Gate MP IDs are shared by every device on the same measurement point list, so live gate
        // selections from the previous device must not carry over. The route keeps this component
        // instance alive when only :installID changes, so both pieces of gate state need resetting
        // here - otherwise the next asset opens with the previous asset's Show All Tabs still on.
        setGateSelections({});
        setShowAllTabs(false);
        initSelectedResultProp(selectedInstall.id);

        // verify that we are editing a device that belongs to this facility
        if (selectedInstall.facilityID !== selectedJob.facilityID) {
            history.push('/jobs');
            return;
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
        // If the install base has changed, updated the selected work order
        initializeWorkOrder();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedInstall.id, selectedJob.id, selectedInstall.facilityID, selectedJob.facilityID]);

    const shouldHideSAPDamageCodeTab = getShouldHideSAPDamageCodeTab();

    /*
     * Move the engineer when the visible tabs change. useLayoutEffect, not useEffect: answering a gate
     * Not In Use on the tab you are looking at is this feature's primary flow, and a passive effect
     * paints TabSelector's "Select from list" placeholder over a blank panel for a frame first. The
     * body only calls setState, so running it before paint costs nothing (MMG-4405).
     */
    React.useLayoutEffect(() => {
        // Nothing has loaded yet - leave the restored tab alone. Resetting here would throw away the
        // tab restored from sessionStorage on every first render.
        if (additionalTabs.length === 0) {
            return;
        }

        const staticTabKeys: Array<string | number> = [TabType.Photos, TabType.Parts];
        if (!shouldHideSAPDamageCodeTab) {
            staticTabKeys.push(TabType.DamageCodes);
        }

        setState(prev => {
            const nextTabKey = resolveNextActiveTab(prev.activeTabKey, visibleTabs, staticTabKeys, initialActiveTabKey);
            return nextTabKey === prev.activeTabKey ? prev : { ...prev, activeTabKey: nextTabKey };
        });
    }, [additionalTabs, visibleTabs, shouldHideSAPDamageCodeTab, initialActiveTabKey]);

    const setForm = (form: AbstractControl, id: string | number) => {
        if (form === null) {
            console.warn('form is null', form, id);
            return;
        }
        /*
         * The in-place mutation, and setForms(forms) below with that same reference, are load-bearing.
         * subscribeToValueChanges' long-lived subscriptions close over syncFormValuesToRedux, which
         * reads forms.forms. Making this immutable would leave those closures pointing at an older,
         * partial forms object: syncFormValuesToRedux would then write a SUBSET of the answers, and
         * updateMprAnswers would take that subset as the complete answer set - silent data loss.
         */
        forms.forms[id] = form;
        const formRef = forms.forms[id];
        if (formRef) {
            formRef.meta = {
                loading: props.loading
            };
        }

        setForms(forms);
        subscribeToValueChanges(forms, id);
    };

    if (!props.selectedMeasurementPointList.id.length) {
        return (
            <Redirect
                to={{
                    pathname: '/devices'
                }}
            />
        );
    }

    // return home if their is no job or the job has been completed or if there is no installBase
    if (
        !props.selectedJob.id.length ||
        (props.selectedJob.status === jobStatusEnum.completed && !isJobReadOnly) ||
        !props.selectedInstall.id.length
    ) {
        return (
            <Redirect
                to={{
                    pathname: '/'
                }}
            />
        );
    }

    if (props.selectedInstall.isDeleted) {
        return <Redirect to={{ pathname: '/devices' }} />;
    }

    if (props.selectedProduct.id.length === 0 || props.history.location.pathname.includes(props.selectedProduct.id)) {
        return <h3>Loading product...</h3>;
    }

    if (props.isMobile || props.isTablet) {
        return (
            <Measurements
                installID={props.match.params.installID}
                jobID={props.selectedJob.id}
                handleCannotComplete={handleCannotComplete}
                shouldHidePhotoTab={getShouldHidePhotosTab()}
                shouldHidePartsTab={getShouldHidePartsTab()}
                shouldHideDamageCodesTab={shouldHideSAPDamageCodeTab}
                selectedInstall={props.selectedInstall}
                selectedJob={props.selectedJob}
                selectedWorkOrders={props.selectedWorkOrders}
                checklistMode={props.checklistMode}
                formControls={formControls}
                historicalWorkOrderID={props.historicalWorkOrderID}
                additionalTabs={additionalTabs}
                visibleTabs={visibleTabs}
                showAllTabs={showAllTabs}
                onToggleShowAllTabs={handleToggleShowAllTabs}
                isHistoricalMode={isHistoricalMode}
                hasNotInUseTabs={hasNotInUseTabs}
                t={props.t}
                getWorkOrderTitle={getWorkOrderTitle}
                history={props.history}
                handleSubmit={handleSubmit}
                setForm={setForm}
                handleActiveTabSelect={handleSelect}
                activeTab={state.activeTabKey}
            />
        );
    }

    return (
        <div>
            <form onSubmit={handleSubmit} className="beacon-form measurement-point-list-form">
                <div className="banner-wrapper">
                    <BannerContainer {...props} />
                    <PointListButtonBarContainer
                        handleCannotComplete={handleCannotComplete}
                        installID={props.match.params.installID}
                        handleSubmit={handleSubmit}
                    />
                </div>
                <div className="mp-tabs-wrapper">
                    {/* Deliberately ahead of <Tabs>: Tabs renders the tablist and the tab panel as
                        adjacent siblings with no slot between them, so the only choices are before the
                        tablist or after the whole measurement point form. Before it is, or a keyboard
                        user has to traverse every field on the active tab to reach the control that
                        changes which tabs exist. The button is absolutely positioned, so DOM order
                        does not affect where it paints. */}
                    <MeasurementPointTabMenu
                        showAllTabs={showAllTabs}
                        onToggleShowAllTabs={handleToggleShowAllTabs}
                        isHistoricalMode={isHistoricalMode}
                        hasNotInUseTabs={hasNotInUseTabs}
                        t={props.t}
                    />
                    <Tabs
                        key={`tabs-${props.match.params.installID}`}
                        activeKey={state.activeTabKey}
                        onSelect={handleSelect}
                        id="controlled-tab-example"
                        animation={false}
                    >
                        {!getShouldHidePhotosTab() && (
                            <Tab eventKey={TabType.Photos} title={'Photos'} className="measurement-point-list">
                                <PhotosControl installID={props.match.params.installID} />
                            </Tab>
                        )}
                        {!getShouldHidePartsTab() && (
                            <Tab
                                eventKey={TabType.Parts} // the number of work order tabs plus the non work order tabs
                                title={'Parts'}
                                className="measurement-point-list"
                            >
                                <PartsControl installID={props.match.params.installID} history={props.history} />
                            </Tab>
                        )}

                        {!shouldHideSAPDamageCodeTab && (
                            <Tab
                                title={'DamageCodes'}
                                eventKey={TabType.DamageCodes}
                                className="measurement-point-list"
                            >
                                <SAPDamageCodes installBase={props.selectedInstall} selectedJob={props.selectedJob} />
                            </Tab>
                        )}

                        {additionalTabs.map((tab: TabSelectorEntity) => {
                            if (tab?.isMeasurementPoint) {
                                return (
                                    <Tab
                                        eventKey={tab.id}
                                        title={tab.title}
                                        key={tab.id}
                                        className="measurement-point-list"
                                        // Gated-off tabs drop out of the nav but keep rendering:
                                        // react-bootstrap builds the pane in a separate pass, and
                                        // unmounting it would drop this tab's saved answers on the
                                        // next updateMprAnswers rebuild. '' rather than undefined -
                                        // exactOptionalPropertyTypes rejects undefined here.
                                        tabClassName={gateHiddenTabIDs.has(tab.id) ? 'mp-tab-hidden' : ''}
                                        disabled={gateHiddenTabIDs.has(tab.id)}
                                    >
                                        <Col xs={12}>
                                            {tab.isChecklistTab && (
                                                <Row>
                                                    <DeficiencyChecklist
                                                        jobID={props.selectedJob.id}
                                                        installBaseID={props.match.params.installID}
                                                    />
                                                </Row>
                                            )}
                                            <Row>
                                                <FormGenerator
                                                    fieldConfig={formControls[tab.id] ?? { controls: {} }}
                                                    onMount={form => setForm(form, tab.id)}
                                                />
                                            </Row>
                                        </Col>
                                    </Tab>
                                );
                            }

                            if (tab?.isWorkOrder) {
                                const selectedWorkOrder: IWorkOrder | undefined = props.selectedWorkOrders.find(
                                    (workOrder: IWorkOrder) => workOrder.id === tab.id
                                );

                                const woTitle = getWorkOrderTitle(selectedWorkOrder, props.t);
                                return (
                                    <Tab
                                        eventKey={tab.id} // Two comes from parts and photos tabs
                                        title={woTitle}
                                        key={tab.id}
                                        className="work-order"
                                    >
                                        <WorkOrderLayout
                                            showChecklist={props.checklistMode}
                                            selectedWorkOrder={selectedWorkOrder ?? initialWorkOrder}
                                            t={props.t}
                                        />
                                    </Tab>
                                );
                            }
                        })}
                    </Tabs>
                </div>
            </form>
        </div>
    );
};

export default MeasurementPointListForm;
